import { cn } from "@/lib/utils";
import { Check, Circle, CircleCheck, Loader } from "lucide-react";
import { forwardRef } from "react";
import { AvatarBadge, IconBadge } from "./badge";
import { TextChip } from "./chip";

// ============================================
// 타입 정의
// ============================================

interface DropdownListItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** 선택된 상태 (violet ring 표시) */
  isSelected?: boolean;
  /** 왼쪽 아이콘 (미지정 시 isSelected에 따라 기본 아이콘 표시) */
  leftIcon?: React.ReactNode;
  /** 오른쪽 아이콘 (선택사항) */
  rightIcon?: React.ReactNode;
  /** 상태 텍스트 (예: "분석 중...") */
  statusText?: string;
  /** 상태 텍스트 앞 아이콘 */
  statusIcon?: React.ReactNode;
  /** 상태 뱃지 표시 여부 (초록색 체크 아이콘) */
  showStatusBadge?: boolean;
  /** 우측 아바타 뱃지 텍스트 (예: "A") */
  avatarBadge?: string;
  children: React.ReactNode;
}

// ============================================
// Separator 컴포넌트
// ============================================

export const DropdownListSeparator = () => {
  return <div className="h-px w-full bg-neutral-200" />;
};

// ============================================
// DropdownListItem 컴포넌트
// ============================================

export const DropdownListItem = forwardRef<
  HTMLButtonElement,
  DropdownListItemProps
>(
  (
    {
      isSelected = false,
      leftIcon,
      rightIcon,
      statusText,
      statusIcon,
      showStatusBadge = false,
      avatarBadge,
      className,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    // 기본 Loader 아이콘 (statusText가 있고 statusIcon이 없을 때)
    const defaultStatusIcon = (
      <Loader className="size-1.5 text-neutral-500" strokeWidth={2} />
    );

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={cn(
          // 기본 레이아웃
          "group relative flex w-full items-center gap-1.5 px-3 py-1.5",
          "rounded-lg overflow-hidden transition-all duration-150",
          "body-2-normal-medium text-primary",
          // Default 상태: 흰색 배경
          "bg-white",
          // Hover 상태: 파란색 배경
          "hover:bg-blue-50 hover:cursor-pointer",
          // Active(Pressed) 상태: 회색 배경
          "active:bg-neutral-100",
          // Disabled 상태
          "disabled:opacity-40 disabled:pointer-events-none",
          // Selected 상태: violet ring
          isSelected && "ring-2 ring-violet-500 ring-inset",
          className,
        )}
        {...props}
      >
        {/* 왼쪽 아이콘 */}
        <span className="flex items-center justify-center size-3.5 shrink-0">
          {leftIcon ??
            (isSelected ? (
              <CircleCheck className="size-3.5 text-slate-600" />
            ) : (
              <Circle className="size-3.5 text-slate-600" />
            ))}
        </span>

        {/* 메인 텍스트 */}
        <span className="flex-1 truncate text-left">{children}</span>

        {/* 오른쪽 아이콘 (선택사항) */}
        {rightIcon && (
          <span className="flex items-center justify-center size-3.5 shrink-0">
            {rightIcon}
          </span>
        )}

        {/* 우측 영역: 상태 뱃지, 상태 텍스트, 아바타 뱃지 */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* 상태 뱃지 (초록색 체크 아이콘) */}
          {showStatusBadge && (
            <IconBadge className="bg-green-500">
              <Check className="size-2 text-white" strokeWidth={3} />
            </IconBadge>
          )}

          {/* 상태 텍스트 칩 */}
          {statusText && (
            <TextChip
              size="small"
              leftIcon={statusIcon ?? defaultStatusIcon}
              className="bg-neutral-100 text-secondary"
            >
              {statusText}
            </TextChip>
          )}

          {/* 아바타 뱃지 */}
          {avatarBadge && <AvatarBadge>{avatarBadge}</AvatarBadge>}
        </div>
      </button>
    );
  },
);

DropdownListItem.displayName = "DropdownListItem";

export default DropdownListItem;
